import { toast } from '@iguanads/react'
import * as Sentry from '@sentry/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRegisterSW } from 'virtual:pwa-register/react'

// Merchants keep the Portal open for days without navigating, and the browser
// only looks for a new service worker on navigation — so ask it to check.
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000

const UPDATE_TOAST_ID = 'pwa-update-prompt'

/**
 * With registerType: 'prompt' (see vite.config.ts), a new service worker
 * installs and waits instead of swapping the app out from under a live
 * session. This raises a persistent toast so the user can reload into the new
 * build. Renders nothing itself.
 */
function PwaUpdatePrompt() {
  const { t } = useTranslation()
  // Counts every waiting worker, so a build that lands after the user
  // dismissed the previous prompt raises it again.
  const [updatesFound, setUpdatesFound] = useState(0)

  const { updateServiceWorker } = useRegisterSW({
    onNeedRefresh() {
      setUpdatesFound((count) => count + 1)
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return

      setInterval(() => {
        if (!navigator.onLine) return

        registration.update().catch((error) => Sentry.captureException(error))
      }, UPDATE_CHECK_INTERVAL_MS)
    },
    onRegisterError(error) {
      Sentry.captureException(error)
    },
  })

  useEffect(() => {
    if (updatesFound === 0) return

    toast(
      {
        type: 'info',
        title: t('components.pwaUpdatePrompt.title'),
        message: t('components.pwaUpdatePrompt.message'),
        // The corner close button dismisses only this toast; Iguana's "Okay"
        // dismisses every open toast
        hideCloseButton: true,
        actionButton: true,
        actionButtonText: t('components.pwaUpdatePrompt.reload'),
        onClickActionButton: () => {
          // Activates the waiting worker; the page reloads once it takes control
          void updateServiceWorker(true)
        },
      },
      {
        toastId: UPDATE_TOAST_ID,
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        pauseOnFocusLoss: false,
      }
    )
  }, [updatesFound, t, updateServiceWorker])

  return null
}

export { PwaUpdatePrompt }
